import { Flag, Check, Clock, ArrowRight, CheckCircle } from "lucide-react";
import { formatDate } from "~/helpers/date";
import { cn } from "~/lib/utils";
import { type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { WorkflowStepStatuses, type WorkflowStepStatus } from "~/api/types";

interface TimelineIndicatorProps {
  icon: LucideIcon;
  text?: string;
  variant?: "primary" | "secondary";
}

export function TimelineIndicator({
  icon: Icon,
  text,
  variant = "primary",
}: TimelineIndicatorProps) {
  return (
    <div className="flex items-center">
      <div
        className={cn(
          "z-10 flex size-8 items-center justify-center rounded-full border bg-white",
          variant === "primary" ? "border-primary/40" : "border-border",
        )}
      >
        <Icon
          className={cn(
            "h-4 w-4",
            variant === "primary" ? "text-primary" : "text-muted-foreground",
          )}
        />
      </div>
      {text && <div className="text-muted-foreground ml-4 text-sm">{text}</div>}
    </div>
  );
}

export function TimelineStart({ startedAt }: { startedAt?: string | null }) {
  const { t } = useTranslation();

  let text = "";
  if (startedAt) {
    text = `${t(($) => $.commons.workflow.startedAt)} ${formatDate(startedAt, "date")}`;
  }

  return <TimelineIndicator icon={Flag} text={text || undefined} />;
}

export function TimelineEnd({
  reviewTo,
  publishedAt,
}: {
  reviewTo?: string | null;
  publishedAt?: string | null;
}) {
  const { t } = useTranslation();

  let text = "";
  if (publishedAt) {
    text = `${t(($) => $.commons.workflow.published)} ${formatDate(publishedAt, "date")}`;
  } else if (reviewTo) {
    text = `${t(($) => $.commons.document.reviewTo)} ${formatDate(reviewTo, "date")}`;
  }

  return <TimelineIndicator icon={CheckCircle} text={text} />;
}

type StepIndicatorProps = {
  value?: string;
  status: WorkflowStepStatus;
};

export function StepIndicator({ value, status }: StepIndicatorProps) {
  const StatusIcon = {
    [WorkflowStepStatuses.completed]: Check,
    [WorkflowStepStatuses.active]: ArrowRight,
    [WorkflowStepStatuses.upcoming]: Clock,
  }[status];

  return (
    <div
      className={cn(
        "z-10 flex size-8 items-center justify-center rounded-full font-medium shadow-md",
        {
          "bg-emerald-700 text-white":
            status === WorkflowStepStatuses.completed,
          "bg-blue-700 text-white": status === WorkflowStepStatuses.active,
          "text-muted-foreground border bg-white shadow-xs":
            status === WorkflowStepStatuses.upcoming,
        },
      )}
    >
      {value ?? <StatusIcon className="h-4 w-4" />}
    </div>
  );
}
